import { useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

/**
 * A list that can fetch its next page.
 *
 * ── THE GAP THIS CLOSES ──
 * The API grew keyset pagination - invoices, customers and expenses each return
 * a `nextCursor` and a sentence saying the list is not the whole list - and
 * every screen kept issuing one unpaged request and rendering whatever came
 * back. Against a real book that is 50 rows out of 2,000, with nothing on the
 * page admitting it. A business could not reach its own invoices from January.
 *
 * Nothing failed, which is why it survived: 50 rows looks like a list, and
 * every test creates fewer than 50.
 *
 * ── WHY `useInfiniteQuery` AND A BUTTON, NOT PAGE NUMBERS ──
 * Keyset pagination has no page numbers to offer - a cursor is "after this
 * exact row", so there is no way to jump to page seven without walking there.
 * Pretending otherwise would mean an offset query underneath, which is the
 * thing the cursor exists to avoid: an OFFSET page shifts under the reader as
 * invoices are raised and payments land, so page two silently repeats or skips
 * rows. A button that says how many more there are is honest about what the
 * data structure can actually do.
 */
export interface PagedResponse<T> {
  items: T[];
  nextCursor?: string;
}

export function usePagedList<T, Extra extends Record<string, unknown> = Record<string, unknown>>(options: {
  /** Query key WITHOUT the cursor - react-query appends the page itself. */
  key: readonly unknown[];
  /** Build the path for a page. The cursor is already encoded. */
  path: (cursor?: string) => string;
  /**
   * Where the next page's cursor lives, when it is not `nextCursor`.
   *
   * The audit log is the exception and is allowed to be: it pages on a
   * timestamp and calls it `nextBefore`, which is the honest name for what it
   * is - `before=<instant>` reads correctly in a URL where `cursor=<opaque>`
   * would be a lie about a value anyone can decode. One list with a different
   * field name is cheaper than renaming a public API field to make a hook
   * tidier.
   */
  cursorOf?: (page: PagedResponse<T> & Extra) => string | undefined;
  enabled?: boolean;
}) {
  const cursorOf = options.cursorOf ?? ((page: PagedResponse<T>) => page.nextCursor);
  const query = useInfiniteQuery({
    queryKey: options.key,
    queryFn: ({ pageParam }) => api.get<PagedResponse<T> & Extra>(options.path(pageParam)),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => cursorOf(last),
    enabled: options.enabled ?? true,
  });

  return {
    ...query,
    /** Every page flattened, in order. */
    items: query.data?.pages.flatMap((page) => page.items) ?? [],
    /** The FIRST page, for the fields that describe the whole result. */
    first: query.data?.pages[0],
  };
}
